import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { lotteryCategories } from '@/mocks/categories';

export default function LotteryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabParam || lotteryCategories[0].slug);

  useEffect(() => {
    if (tabParam) {
      setActiveTab(tabParam);
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [tabParam]);

  const activeCategory = lotteryCategories.find((c) => c.slug === activeTab) || lotteryCategories[0];

  const handleTabChange = (slug: string) => {
    setActiveTab(slug);
    setSearchParams({ tab: slug });
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <section className="w-full px-4 md:px-6 py-8 bg-background-50">
          <Breadcrumb
            items={[
              { label: '彩票大厅', path: '/lottery' },
              { label: activeCategory.name },
            ]}
          />
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-2">
                彩票大厅
              </h1>
              <p className="text-foreground-600 text-sm">
                选择您喜欢的彩票玩法，开启幸运之旅
              </p>
            </div>

            <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-8 flex-wrap">
              {lotteryCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleTabChange(cat.slug)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                    activeTab === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-base`}></i>
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden mb-6">
                  <div className="h-56 md:h-72 overflow-hidden relative">
                    <img
                      src={activeCategory.thumbnailUrl}
                      alt={activeCategory.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                    <div className="absolute bottom-4 left-6">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-12 h-12 rounded-xl flex items-center justify-center bg-white/20 backdrop-blur-sm"
                        >
                          <i className={`${activeCategory.icon} text-2xl text-white`}></i>
                        </div>
                        <div>
                          <h2 className="font-heading font-bold text-2xl text-white">
                            {activeCategory.name}
                          </h2>
                          <p className="text-white/70 text-sm">
                            {activeCategory.shortDesc}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="text-foreground-700 text-sm leading-relaxed">
                      {activeCategory.description}
                    </p>
                    <div className="flex items-center gap-4 mt-4 text-sm text-foreground-500">
                      <span className="flex items-center gap-1.5">
                        <i className="ri-calendar-line text-primary-500"></i>
                        {activeCategory.drawDays}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <i className="ri-time-line text-primary-500"></i>
                        开奖时间：{activeCategory.drawTime}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <i className="ri-money-cny-circle-line text-primary-500"></i>
                        {activeCategory.minPrice}元/注
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 mb-6">
                  <h3 className="font-heading font-semibold text-foreground-950 text-base mb-4 flex items-center gap-2">
                    <i className="ri-book-open-line text-primary-500"></i>
                    玩法规则
                  </h3>
                  <ul className="space-y-2.5">
                    {activeCategory.rules.map((rule, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-foreground-700 text-sm">
                        <span className="w-5 h-5 rounded-full bg-primary-100 text-primary-600 text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                          {idx + 1}
                        </span>
                        {rule}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 mb-6">
                  <h3 className="font-heading font-semibold text-foreground-950 text-base mb-4 flex items-center gap-2">
                    <i className="ri-trophy-line text-primary-500"></i>
                    奖级设置
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-background-200/70">
                          <th className="text-left py-3 px-3 text-foreground-500 font-medium text-xs">奖级</th>
                          <th className="text-left py-3 px-3 text-foreground-500 font-medium text-xs">奖金</th>
                          <th className="text-left py-3 px-3 text-foreground-500 font-medium text-xs">中奖条件</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeCategory.odds.map((odd, idx) => (
                          <tr key={idx} className="border-b border-background-100">
                            <td className="py-3 px-3 text-foreground-950 font-medium">{odd.prize}</td>
                            <td className="py-3 px-3 text-accent-600 font-semibold">{odd.amount}</td>
                            <td className="py-3 px-3 text-foreground-600 text-xs">{odd.condition}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="bg-background-50 rounded-xl border border-background-200/70 p-6">
                  <h3 className="font-heading font-semibold text-foreground-950 text-base mb-4 flex items-center gap-2">
                    <i className="ri-lightbulb-line text-primary-500"></i>
                    购彩贴士
                  </h3>
                  <ul className="space-y-2">
                    {activeCategory.tips.map((tip, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-foreground-700 text-sm">
                        <i className="ri-checkbox-circle-line text-primary-500 flex-shrink-0 mt-0.5"></i>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="lg:col-span-1">
                {activeCategory.latestResult && (
                  <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 mb-5">
                    <h3 className="font-heading font-semibold text-foreground-950 text-base mb-4 flex items-center gap-2">
                      <i className="ri-history-line text-primary-500"></i>
                      最新开奖
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-foreground-500">期号</span>
                        <span className="text-foreground-950 font-medium">
                          {activeCategory.latestResult.period}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-foreground-500">开奖日期</span>
                        <span className="text-foreground-950 font-medium">
                          {activeCategory.latestResult.date}
                        </span>
                      </div>
                      <div>
                        <div className="text-foreground-500 text-sm mb-2">开奖号码</div>
                        <div className="flex items-center gap-2 flex-wrap">
                          {activeCategory.latestResult.numbers.map((num, idx) => (
                            <span
                              key={idx}
                              className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white ${
                                idx === activeCategory.latestResult!.numbers.length - 1 &&
                                activeCategory.slug === 'shuangseqiu'
                                  ? 'bg-blue-500'
                                  : 'bg-accent-500'
                              }`}
                            >
                              {num}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 mb-5">
                  <h3 className="font-heading font-semibold text-foreground-950 text-base mb-4 flex items-center gap-2">
                    <i className="ri-smartphone-line text-primary-500"></i>
                    如何购买
                  </h3>
                  <ol className="space-y-3">
                    <li className="flex items-start gap-3 text-sm">
                      <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-600 text-xs font-bold flex items-center justify-center flex-shrink-0">
                        1
                      </span>
                      <span className="text-foreground-700">下载BB彩票APP并完成注册认证</span>
                    </li>
                    <li className="flex items-start gap-3 text-sm">
                      <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-600 text-xs font-bold flex items-center justify-center flex-shrink-0">
                        2
                      </span>
                      <span className="text-foreground-700">在彩票大厅中选择{activeCategory.name}</span>
                    </li>
                    <li className="flex items-start gap-3 text-sm">
                      <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-600 text-xs font-bold flex items-center justify-center flex-shrink-0">
                        3
                      </span>
                      <span className="text-foreground-700">选号或机选号码，确认投注即可</span>
                    </li>
                  </ol>
                </div>

                <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl p-6 text-white">
                  <h3 className="font-heading font-semibold text-base mb-3">
                    下载BB彩票APP
                  </h3>
                  <p className="text-white/80 text-sm mb-4">
                    支持双色球、大乐透、快三等所有彩票玩法，安全正规，极速到账
                  </p>
                  <a
                    href="#download"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-primary-600 font-semibold rounded-full text-sm whitespace-nowrap cursor-pointer hover:bg-white/90 transition-colors"
                  >
                    <i className="ri-download-2-line"></i>
                    免费下载
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}